/* eslint-disable spaced-comment */
import React, { Component, PropTypes } from "react"
import { reduxForm } from "redux-form"
import get from "lodash/get"
import cloneDeep from "lodash/cloneDeep"
import { generateClientDicts } from "lib/helpers"
import { validateClientProfileDataForm } from "./validation"
import { fetchClientOptionsDict, updateEasinsureClient } from "state/actions"
import Input from "components/input"
import { FormField } from "components/shared/form-field"
import { SearchableSelectWrapper } from "components/shared/select/SelectWrap"
import { ClientProfileDataFormList } from "./ClientProfileDataFormList"
import { DatePicker } from "components/shared/datePicker"
import InfoModal from "components/generalComponents/infoModal"
import { intl, translate } from "./translation"
import { formatReadOnlyClientData } from "./helpers"
import styles from './ClientProfileDataForm.scss'
import stylesActions from '../ClientProfileDataWrapper.scss'

export const clientProfileFields = [
    "country",
    "postalCode",
    "town",
    "street",
    "number",
    "bus",
    "emails",
    "phoneNumbers",
    "birthDate",
    "gender",
    "language",
]

const createInitialValues = (main, detail, dictionary, language) => {
    const initialValues = {
        country: "",
        postalCode: "",
        town: "",
        street: "",
        number: "",
        bus: "",
        emails: [],
        phoneNumbers: [],
        birthDate: "",
        gender: "",
        language: "",
    }

    if (!main && !detail) return initialValues // eslint-disable-line curly

    const clientOptionsDict = generateClientDicts(dictionary, language)

    initialValues.postalCode = get(main, "mainAddress.postalCode", "")
    initialValues.street = get(main, "mainAddress.street", "")
    initialValues.number = get(main, "mainAddress.number", "")
    initialValues.bus = get(main, "mainAddress.extension", "")
    initialValues.birthDate = get(main, "dateOfBirth", "")

    let country = get(main, "mainAddress.country", "")
    get(clientOptionsDict, 'globalCountry', []).forEach(({value, label}) => {
        if (Object.values(label).includes(country)) {
            country = value // set value (country code) instead of label
            return
        }
    })
    initialValues.country = country // store country code as initial value

    let town = get(main, "mainAddress.locality", "")
    if (country === "1") { // if country is Belgium
        // combine postal code and locality
        const localityValue = `${initialValues.postalCode}&${town}`
        const birthLocality = get(clientOptionsDict, 'birthLocality', [])
        const localityIndex = birthLocality.findIndex(({value}) => value === localityValue)
        if (localityIndex !== -1) {
            town = birthLocality[localityIndex].value
        }
    }
    initialValues.town = town // store locality code as initial value

    const emails = get(detail, "emails", [])
    initialValues.emails = cloneDeep(emails)

    const phoneNumbers = get(detail, "phoneNumbers", [])
    initialValues.phoneNumbers = cloneDeep(phoneNumbers)

    let gender = get(detail, "gender", "")
    get(clientOptionsDict, 'gender', []).forEach(({value, label}) => {
        if (gender === label.substring(0, 1)) {
            gender = value
        }
    })
    initialValues.gender = gender

    let clientLanguage = get(detail, "language", "")
    get(clientOptionsDict, 'language', []).forEach(({label, value}) => {
        if (clientLanguage === label) {
            clientLanguage = value
        }
    })
    initialValues.language = clientLanguage

    return initialValues
}

@reduxForm({
    form: "clientProfileDataForm",
    fields: clientProfileFields,
    validate: validateClientProfileDataForm,
    destroyOnUnmount: true,
},
({ app, client: { main, detail, clientOptionsDict }}) => ({
    initialValues: createInitialValues(main, detail, clientOptionsDict, app.lang),
    appLanguage: app.lang,
    main,
    detail,
    clientOptionsDict,
}))
export class ClientProfileDataForm extends Component {
    // ********************************************************************************
    //#region - Component Lifecycle Methods
    // ********************************************************************************
    constructor(props) {
        super(props)
        this.state = { clientDictionary: null, informModalOpen: false }
    }

    componentWillMount() {
        const { dispatch, clientOptionsDict } = this.props
        if (!clientOptionsDict || Object.keys(clientOptionsDict).length === 0) {
            dispatch(fetchClientOptionsDict()).then((dictionary) => {
                this.setClientDictionary(dictionary)
            })
            return
        }
        this.setClientDictionary(clientOptionsDict)
    }
    //#endregion - Component Lifecycle Methods

    // ********************************************************************************
    //#region - Helper Methods
    // ********************************************************************************
    setClientDictionary = (dictionary) => {
        const { appLanguage } = this.props
        this.setState({ clientDictionary: generateClientDicts(dictionary, appLanguage) })
    }

    handleClientProfileDataFormSubmit = (values) => {
        const { dispatch, onCancel, onSubmitData, main, detail } = this.props
        const { clientDictionary } = this.state

        if (values.country === "1") { // if country is Belgium
            const postalCodeTown = values.town && values.town.split("&")
            values.postalCode = postalCodeTown[0] // set postal code from locality select list
            values.town = postalCodeTown[1] // set locality (city) from locality select list
        }

        const readOnlyData = formatReadOnlyClientData(main, detail, clientDictionary)
        values.main = readOnlyData.main
        values.detail = readOnlyData.detail

        dispatch(updateEasinsureClient(values)).then((result) => {
            if (result && result.error === 0) {
                onSubmitData(translate(intl.modifyClientMyEasinsureSuccess), 'success')
            }
            if (result && result.error === 1) {
                onSubmitData(translate(intl.modifyClientMyEasinsureError), 'error')
            }
            if (result && result.error === 2) {
                onSubmitData(translate(intl.modifyClientMyEasinsureErrorGeneral), 'error')
            }
            onCancel()
        })
    }

    handleClickCancel = () => {
        const { dirty, onCancel } = this.props
        if (dirty) {
            this.setState({ informModalOpen: true })
            return
        }
        onCancel()
    }

    handleCountryChange = (newValue) => {
        const { fields: { town, postalCode, country }} = this.props
        if (country.value === "1") { // if country is Belgium
            town.onChange("") // clear locality
            postalCode.onChange("") // clear postal code
        }
        country.onChange(newValue)
    }
    //#endregion - Helper Methods

    // ********************************************************************************
    //#region - Render
    // ********************************************************************************
    render() {
        const { clientDictionary, informModalOpen } = this.state
        const {
            fields: { gender, birthDate, language, street, number, bus, postalCode, town, country, emails, phoneNumbers },
            handleSubmit,
            pristine,
            invalid,
            onCancel,
            main,
        } = this.props

        const isBelgium = country.value === '1'

        return (
            <form onSubmit={handleSubmit(this.handleClientProfileDataFormSubmit)}>
                <div className={stylesActions.actionsContainer} style={{marginBottom: '1rem'}}>
                    <button
                        type="submit"
                        className={stylesActions.saveClientProfileData}
                        disabled={pristine || invalid}
                    >
                        {translate(intl.save)}
                    </button>
                    <button
                        type="button"
                        className={stylesActions.toggleEditBtn}
                        onClick={this.handleClickCancel.bind(this)}
                    >
                        {translate(intl.cancel)}
                    </button>
                </div>
                <FormField label={translate(intl.lastName)}>
                    {`${main.firstName} ${main.lastName}`}
                </FormField>
                <FormField
                    label={translate(isBelgium ? intl['postCode&Locality'] : intl.locality)}
                    field={town}
                    className={styles.formFieldLocality}
                >
                    {isBelgium ? (
                        <SearchableSelectWrapper
                            isSearchable
                            value={town.value || ""}
                            id={translate(intl.locality)}
                            name={translate(intl.locality)}
                            options={get(clientDictionary, 'birthLocality', [])}
                            onChange={town.onChange} // eslint-disable-line react/jsx-handler-names
                            className={styles.select}
                        />
                    ) : (
                        <Input
                            type="text"
                            placeholder={translate(intl.locality)}
                            id={translate(intl.locality)}
                            name={translate(intl.locality)}
                            value={town.value || ""}
                            onChange={town.onChange} // eslint-disable-line react/jsx-handler-names
                            onBlur={town.onBlur} // eslint-disable-line react/jsx-handler-names
                            className={styles.input}
                        />
                    )}
                </FormField>
                {!isBelgium && (
                    <FormField label={translate(intl.postalCode)} field={postalCode}>
                        <Input
                            type="text"
                            placeholder={translate(intl.postalCode)}
                            id={translate(intl.postalCode)}
                            name={translate(intl.postalCode)}
                            value={postalCode.value || ""}
                            onChange={postalCode.onChange} // eslint-disable-line react/jsx-handler-names
                            onBlur={postalCode.onBlur} // eslint-disable-line react/jsx-handler-names
                            className={styles.input}
                        />
                    </FormField>
                )}
                <FormField label={translate(intl.street)} field={street}>
                    <Input
                        type="text"
                        placeholder={translate(intl.street)}
                        id={translate(intl.street)}
                        name={translate(intl.street)}
                        value={street.value || ""}
                        onChange={street.onChange} // eslint-disable-line react/jsx-handler-names
                        onBlur={street.onBlur} // eslint-disable-line react/jsx-handler-names
                        className={styles.input}
                    />
                </FormField>
                <FormField label={translate(intl.number)} field={number}>
                    <Input
                        type="text"
                        placeholder={translate(intl.number)}
                        id={translate(intl.number)}
                        name={translate(intl.number)}
                        value={number.value || ""}
                        onChange={number.onChange} // eslint-disable-line react/jsx-handler-names
                        onBlur={number.onBlur} // eslint-disable-line react/jsx-handler-names
                        className={styles.input}
                    />
                </FormField>
                <FormField label={translate(intl.extension)} field={bus}>
                    <Input
                        type="text"
                        placeholder={translate(intl.extension)}
                        id={translate(intl.extension)}
                        name={translate(intl.extension)}
                        value={bus.value || ""}
                        onChange={bus.onChange} // eslint-disable-line react/jsx-handler-names
                        onBlur={bus.onBlur} // eslint-disable-line react/jsx-handler-names
                        className={styles.input}
                    />
                </FormField>
                <FormField label={translate(intl.country)} field={country}>
                    <SearchableSelectWrapper
                        isSearchable
                        value={country.value || ""}
                        id={translate(intl.country)}
                        name={translate(intl.country)}
                        options={get(clientDictionary, 'country', [])}
                        onChange={this.handleCountryChange}
                        className={styles.select}
                    />
                </FormField>
                <FormField
                    label={translate(intl.dateOfBirth)}
                    field={birthDate}
                    className={styles.formFieldDateOfBirth}
                >
                    <DatePicker
                        value={birthDate.value || ""}
                        id={translate(intl.dateOfBirth)}
                        name={translate(intl.dateOfBirth)}
                        onChange={birthDate.onChange} // eslint-disable-line react/jsx-handler-names
                    />
                </FormField>
                <FormField label={translate(intl.gender)} field={gender}>
                    <SearchableSelectWrapper
                        isSearchable
                        value={gender.value || ""}
                        id={translate(intl.gender)}
                        name={translate(intl.gender)}
                        options={get(clientDictionary, 'gender', [])}
                        onChange={gender.onChange} // eslint-disable-line react/jsx-handler-names
                        className={styles.select}
                    />
                </FormField>
                <FormField label={translate(intl.language)} field={language}>
                    <SearchableSelectWrapper
                        isSearchable
                        value={language.value || ""}
                        id={translate(intl.language)}
                        name={translate(intl.language)}
                        options={get(clientDictionary, 'language', [])}
                        onChange={language.onChange} // eslint-disable-line react/jsx-handler-names
                        className={styles.select}
                    />
                </FormField>
                <FormField label={translate(intl.emails)} field={emails}>
                    <ClientProfileDataFormList
                        emails={emails.value || []}
                        onChange={emails.onChange} // eslint-disable-line react/jsx-handler-names
                        initialValue={emails.initialValue || []}
                    />
                </FormField>
                <FormField label={translate(intl.phoneNumbers)} field={phoneNumbers}>
                    <ClientProfileDataFormList
                        phoneNumbers={phoneNumbers.value || []}
                        onChange={phoneNumbers.onChange} // eslint-disable-line react/jsx-handler-names
                        initialValue={phoneNumbers.initialValue || []}
                    />
                </FormField>
                <InfoModal
                    isOpen={Boolean(informModalOpen)}
                    title={translate(intl.modalConfirmCancel)}
                    content={translate(intl.modalConfirmCancelContent)}
                    onCancel={() => {this.setState({ informModalOpen: false })}}
                    onConfirmation={onCancel}
                    cancelLabel={translate(intl.cancel)}
                    confirmationLabel={translate(intl.ok)}
                    options={{noCancelButton: false}}
                />
            </form>
        )
    }
    //#endregion - Render
}
ClientProfileDataForm.propTypes = {
    clientOptionsDict: PropTypes.object,
    appLanguage: PropTypes.string,
    main: PropTypes.object,
    detail: PropTypes.object,
    dispatch: PropTypes.func,
    onCancel: PropTypes.func,
    onSubmitData: PropTypes.func,
    dirty: PropTypes.bool,
    fields: PropTypes.object,
    handleSubmit: PropTypes.func,
    invalid: PropTypes.bool,
    pristine: PropTypes.bool,
}



// WEBPACK FOOTER //
// ./components/easinsure/client-profile/client-profile-form/ClientProfileDataForm.jsx